import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {Box, Button, Grid} from '@mui/material';
import moment from 'moment';
import {useSnackbar} from 'notistack';
import React, {PropsWithChildren, useCallback, useEffect, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {differenceInYears} from 'date-fns';
import AutoField, {FormField} from '../../components/AutoForm/AutoFieldNew';
import withAutoFormNew, {FormReadOnly} from '../../components/AutoForm/AutoFormNew';
import {useAutoField, useAutoForm} from '../../components/AutoForm/useAutoForm';
import {HandoverFooterNavigation} from '../../components/FooterNavigation/HandoverFooterNavigation';
import {HandoverStep} from '../../components/HandoverStep/HandoverStep';
import Loading from '../../components/Loading/Loading';
import {useAccountContext} from '../../contexts/AccountContext';
import {useReviewContext} from '../../contexts/ReviewContext';
import useUpdatePrecursor, {
  useContactIdAndReviewFor,
} from '../../hooks/useUpdatePrecursor';
import {ReviewDetailsFragment} from '../../schema-types/ReviewDetailsFragment';
import {newUTCDate} from '../../utils/newUTCDate';
import {
  headerFieldsType,
  precursorAndHeaderFieldsType,
} from '../PrecursorWizard/fields/precursorFields';
import {SummarySection} from '../HandoverSummaryPage/SummarySection';
import {ReviewReason, getContactAttribution, useGetHeaderFields} from './headerFields';
import {useContactStatuses} from './useContactStatuses';
import {AccountBasicFragment} from '../../schema-types/AccountBasicFragment';
import {ReviewStatusOneviewEnum} from '../../schema-types/globalTypes';
import {SaltusAny} from '../../utils/SaltusAny';
import {ErrorPage} from '../ErrorPage';
import validateUUID from '../../utils/validateUUID';

export const reasonDisplay: Record<string, string> = {
  [ReviewReason.adHoc]: 'Ad hoc',
  [ReviewReason.annual]: 'Annual',
  [ReviewReason.initial]: 'Initial',
};

type HeaderFormProps = {
  accountDetails: AccountBasicFragment;
  review: ReviewDetailsFragment;
  updateLoading: boolean;
  readOnly?: boolean;
  showFooter: boolean;
};

export function PrecursorSummaryPage() {
  const {reviewDetails} = useReviewContext();
  const navigate = useNavigate();
  const shouldRedirect = useMemo(() => {
    if (!reviewDetails) {
      return null;
    }
    if (
      reviewDetails?.precursorNotFinished &&
      reviewDetails.statusOneview !== ReviewStatusOneviewEnum.CLOSED &&
      reviewDetails.statusOneview !== ReviewStatusOneviewEnum.COMPLETED
    ) {
      return true;
    }
    return false;
  }, [reviewDetails]);

  useEffect(() => {
    if (shouldRedirect) {
      navigate({pathname: '../header-precursor'});
    }
  }, [shouldRedirect]);

  if (shouldRedirect === null || shouldRedirect === true) {
    return null;
  }

  if (!reviewDetails) {
    return <ErrorPage code="404" text="Review not found" />;
  }

  return (
    <>
      <Box mt={2} />
      <HandoverStep />
      <PrecursorSummaryInner showFooter />
      <Box mt={2} />
    </>
  );
}

export const PrecursorSummaryInner: React.FC<
  PropsWithChildren<{showFooter: boolean}>
> = ({children, showFooter = false}) => {
  const {account, accountId} = useAccountContext();
  const {enqueueSnackbar} = useSnackbar();
  const {reviewDetails} = useReviewContext();
  const {contactStatuses} = useContactStatuses(accountId!);
  const formFields = useGetHeaderFields(
    account,
    contactStatuses?.accountDetails || null,
    reviewDetails
  );

  const onError = useCallback(
    (msg: SaltusAny) =>
      enqueueSnackbar(`Could not update review. Please try again. Error: ${msg}`, {
        variant: 'error',
        autoHideDuration: null,
      }),
    [enqueueSnackbar]
  );

  const {loading: updateLoading} = useUpdatePrecursor({
    reviewId: reviewDetails?.id,
    onSuccess: (x) => {},
    onError,
  });

  const {values: valuesReview} = useAutoForm<precursorAndHeaderFieldsType>();
  const {reviewFor, contactId} = useContactIdAndReviewFor(valuesReview);

  const initialValues = useMemo<Partial<headerFieldsType>>(() => {
    if (!reviewDetails || !account) return {};
    return {
      reviewReason: reviewDetails.reviewReason,
      reviewAction: reviewDetails.reviewAction,
      ...(reviewDetails.dateBooked ? {bookedMeetingDate: reviewDetails.dateBooked} : {}),
      ...(reviewDetails.dateScheduled
        ? {plannedMeetingDate: reviewDetails.dateScheduled}
        : {}),
      ...(reviewDetails.focus ? {reviewFocus: reviewDetails.focus} : {reviewFocus: ''}),
      ...(reviewDetails.meetingType ? {meetingType: reviewDetails.meetingType} : {}),
      opportunityValue: reviewDetails.opportunityValue || 0,
      name: reviewDetails.name?.replace(/undefined/gi, '') || '',
      reviewContact: getContactAttribution(account, reviewDetails),
      reviewFor,
      contactId: validateUUID(contactId),
      primaryAtrValue: contactStatuses?.accountDetails.primaryContact?.atr?.finalValue,
      // eslint-disable-next-line no-nested-ternary
      primaryAtrDate: contactStatuses?.accountDetails.primaryContact?.atr?.finalValue
        ? contactStatuses?.accountDetails.primaryContact?.atr?.dateSubmitted
        : undefined,
      secondaryAtrValue:
        contactStatuses?.accountDetails.secondaryContact?.atr?.finalValue,
      secondaryAtrDate: contactStatuses?.accountDetails.secondaryContact?.atr?.finalValue
        ? contactStatuses?.accountDetails.secondaryContact?.atr?.dateSubmitted
        : undefined,
      status: reviewDetails?.status,
      previousCommunications: reviewDetails?.clientCommunicationAttempts || [],
      // ...getContactFieldValues(vulnerabilities),
    };
  }, [reviewDetails, account, contactStatuses]);

  if (!reviewDetails || !account) {
    return <Loading />;
  }

  return (
    <>
      <HeaderForm
        showFooter={showFooter}
        formFields={formFields}
        accountDetails={account}
        review={reviewDetails}
        submit={(values) => {
          // return updateReview(reviewId!, values, {inPrecursor: false});
        }}
        initialValueOverrides={initialValues}
        updateLoading={updateLoading}
        validate={(values) => {
          const errors: Record<string, string> = {};
          if (values.status === 'Meeting booked') {
            if (moment.utc(values.dateBooked).isBefore(Date.now())) {
              errors.dateBooked = 'Booked date has already happened';
            }
          }
          return errors;
        }}
      >
        {children}
      </HeaderForm>
    </>
  );
};

export const HeaderForm = withAutoFormNew<
  headerFieldsType,
  PropsWithChildren<HeaderFormProps>
>(
  ({
    formFields,
    accountDetails,
    review,
    updateLoading,
    readOnly = true,
    children,
    showFooter,
  }) => {
    const {values} = useAutoForm<headerFieldsType>();
    const {submitForm} = useAutoForm();
    const headerFormInner = (
      <>
        <Grid container wrap="wrap">
          <FormReadOnly readOnly>
            {formFields?.map((field) => {
              return (
                <Grid item xs={6} xl={4} key={field.id}>
                  <HeaderField field={field} />
                </Grid>
              );
            })}
          </FormReadOnly>
        </Grid>
      </>
    );

    return (
      <FormReadOnly readOnly>
        {children}
        <Grid container spacing={5}>
          <Grid item xs={3} lg={2}>
            <Box height="100%" display="flex" flexDirection="column">
              <SummarySection
                fullHeight
                step="meeting"
                editable={false}
                title="Edit meeting"
              >
                <Button
                  disabled={
                    review.statusOneview === ReviewStatusOneviewEnum.CLOSED ||
                    review.statusOneview === ReviewStatusOneviewEnum.COMPLETED
                  }
                  variant="contained"
                  color="secondary"
                  data-testid="EditButton"
                  href="./header-precursor"
                >
                  Edit meeting values
                </Button>
              </SummarySection>
            </Box>
          </Grid>
          <Grid item xs={9} lg={10}>
            <SummarySection
              editable={false}
              step="meeting"
              title={`${accountDetails.name || ''} meeting details`}
            >
              <Box mt={1} />
              {headerFormInner}
            </SummarySection>
          </Grid>
        </Grid>
        {/* <FeatureBoundary
          showErrorIfDenied={false}
          features={[GlobalPermissionEnum.can_access_xperidoc]}
        >
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <DocumentGenerator entity="t4a_review" entityId={review.curoId} />
            </Grid>
          </Grid>
        </FeatureBoundary> */}
        {showFooter ? (
          <HandoverFooterNavigation
            onExit={submitForm}
            onNext={submitForm}
            onNextLoading={updateLoading}
            nextIcon={<TrendingUpIcon />}
          />
        ) : null}
      </FormReadOnly>
    );
  },
  {enableReinitialize: true},
  {debugPosition: 'bottom'}
);

const HeaderField = ({field}: {field: FormField}) => {
  const [{value}, _, helpers] = useAutoField(field.id);
  const checkError = useMemo(() => {
    return ['primaryAtrDate', 'secondaryAtrDate'].includes(field.id);
  }, [field.id]);

  const ATRDateInvalid = useMemo(() => {
    if (checkError) {
      try {
        const atrDate = newUTCDate(value);
        if (Math.abs(differenceInYears(atrDate, newUTCDate())) >= 3) {
          helpers.setError('Over 3 years ago');
          return true;
        }
      } catch (err) {
        return true;
      }
    }
    return false;
  }, [checkError, value]);
  return (
    <Box display="flex" ml={1} mb={2} mr={1}>
      <AutoField error={ATRDateInvalid} field={field} />
    </Box>
  );
};
